import { isNative } from '../../../../../services/JWeb';
import { Action, Location, UnregisterCallback } from 'history';
import EventNames from '../../../../../config/eventNames';
import {
  EventDataActionEnum,
  EventDataActivityEnum,
  EventDataControlNameEnum,
  EventDataScreenNameEnum,
  EventDataScreenPathEnum,
  EventSchemaVersionEnum
} from '../../../../../services/AnalyticsService/dependencies/AnalyticsEventEnums';
import getOSDetailsFromBrowser from '../../../../../utils/getOSDetailsFromBrowser';
import * as T from './types';
import { getServices } from '../../../../../infra/startup';
import { TFunction } from 'i18next';
import bindAllMethods from '../../../../../utils/bindAllMethods';
import IEventService from '../../../../../services/eventService/IEventService';
import { LOGIN_PATH_LIST } from '../../../../../services/session/constants';
import { internalLogger } from '../../../../../interface/v1/clientOsLogger';
import ResumeSessionModalLauncher from './ResumeSessionModalLauncher';
import {
  DISCARDED_SESSION_STORAGE_KEY,
  RESUME_SESSION_MODAL_ASSET_REFERENCE
} from './constants';

const RESUME_SESSION_MODAL_KEY = 'onboarding-confirm-before-leave-modal';

export interface ListenerManagerConstructorProperties {
  storeInterface: T.OnboardingAgentConstructorDependencies['storeInterface'];
  localizationInterface: T.OnboardingAgentConstructorDependencies['localizationInterface'];
  navigationInterface: T.OnboardingAgentConstructorDependencies['navigationInterface'];
  resumeSessionModalLauncher?: ResumeSessionModalLauncher;
}

export default class ListenerManager {
  private _eventService: IEventService;
  private _storeInterface: T.OnboardingAgentConstructorDependencies['storeInterface'];
  private _localizationInterface: T.OnboardingAgentConstructorDependencies['localizationInterface'];
  private _navigationInterface: T.OnboardingAgentConstructorDependencies['navigationInterface'];
  private _removeConfirmBeforeLeaveListener?: () => void;
  private _globalTranslate: TFunction;
  private _removeNavigationBlocker: UnregisterCallback;
  private _confirmed: boolean;
  private _resumeSessionModalLauncher: ResumeSessionModalLauncher;
  private lastAction: Action;
  private lastLocation: Location;

  constructor({
    storeInterface,
    localizationInterface,
    navigationInterface,
    resumeSessionModalLauncher
  }: ListenerManagerConstructorProperties) {
    const { eventService } = getServices();
    this._eventService = eventService;
    this._storeInterface = storeInterface;
    this._localizationInterface = localizationInterface;
    this._navigationInterface = navigationInterface;
    this._resumeSessionModalLauncher =
      resumeSessionModalLauncher ??
      new ResumeSessionModalLauncher(RESUME_SESSION_MODAL_KEY);
    bindAllMethods(this);
    //this.getNavigationPrompt.bind(this);
  }

  private async translate(key: string | undefined, defaultValue: string) {
    if (!this._globalTranslate) {
      this._globalTranslate =
        await this._localizationInterface?.getGlobalTranslatorFunction?.();
    }
    if (this._globalTranslate && key) {
      return this._globalTranslate?.(key, defaultValue);
    } else {
      return defaultValue;
    }
  }

  private getConfirmBeforeLeave = async () => {
    const manifest = this._storeInterface?.state?.manifest;

    const {
      message: manifestMessage,
      translationKey,
      enable,
      assetReference
    } = manifest?.services?.onboarding?.confirmBeforeLeave || {};

    const hardCodedDefaultMessage =
      'Please stay on this screen. If you select OK, you may lose your progress. To continue, select Cancel.';

    const defaultMessage =
      typeof manifestMessage === 'string'
        ? manifestMessage
        : hardCodedDefaultMessage;

    return {
      enable: !!enable,
      message: await this.translate?.(translationKey, defaultMessage),
      assetReference:
        typeof assetReference === 'string'
          ? assetReference
          : RESUME_SESSION_MODAL_ASSET_REFERENCE
    };
  };

  private beforeunloadCallback = async (event: BeforeUnloadEvent) => {
    const { enable, message } = (await this.getConfirmBeforeLeave?.()) || {};
    const isLoginPath = LOGIN_PATH_LIST?.some?.((pathname) =>
      this._navigationInterface.location.pathname.startsWith(pathname)
    );

    if (!this._confirmed && enable && !isLoginPath) {
      event.returnValue = message;
      event.preventDefault();
      return message;
    }
  };

  private pagehideCallback = async (event: BeforeUnloadEvent) => {
    const { enable, message } = await this.getConfirmBeforeLeave();
    const isLoginPath = LOGIN_PATH_LIST?.some?.((pathname) =>
      this._navigationInterface.location.pathname.startsWith(pathname)
    );

    if (!this._confirmed && enable && !isLoginPath && confirm(message)) return;

    event.preventDefault();
  };

  private setListener = (eventName: 'pagehide' | 'beforeunload', callback) => {
    window.addEventListener(eventName, callback, {
      capture: true
    });

    this._removeConfirmBeforeLeaveListener = () => {
      this._removeNavigationBlocker();
      window.removeEventListener(eventName, callback, {
        capture: true
      });
    };
  };

  private createSimpleUiEventData({ action, controlName }) {
    return {
      action,
      activity: EventDataActivityEnum.allV01,
      screenPath: EventDataScreenPathEnum.portalOobe,
      screenName: EventDataScreenNameEnum.backButtonAlert,
      controlName,
      version: EventSchemaVersionEnum.simpleUi
    };
  }

  /**
   * Shows the Resume Session Modal MFE (via ResumeSessionModalLauncher) and
   * resolves to true when the user picks "Start a New Session" (leave), or
   * false when the user picks "Resume" (stay). Falls back to the native
   * confirm() dialog if the modal cannot be loaded, so back-button protection
   * never breaks. `assetReference` follows the same manifest-overridable
   * pattern used elsewhere in OnboardingAgent (see SessionManager's
   * serviceFallback.modalContent).
   */
  private async confirmLeaveWithResumeSessionModal(
    message: string,
    assetReference: string = RESUME_SESSION_MODAL_ASSET_REFERENCE
  ): Promise<boolean> {
    const sessionContext = this._storeInterface?.state?.onboarding
      ?.sessionContext as { updatedAt?: string } | undefined;

    try {
      const { selection } = await this._resumeSessionModalLauncher.open({
        session: {
          lastUpdatedAt: sessionContext?.updatedAt || new Date().toISOString()
        },
        assetReference
      });

      return selection === 'start-new';
    } catch (error) {
      internalLogger?.error?.(
        'ListenerManager - confirmLeaveWithResumeSessionModal - failed to load Resume Session Modal MFE, falling back to confirm()',
        error
      );

      return confirm(message);
    }
  }

  async getNavigationPrompt(location: Location, action: Action) {
    const { enable, message, assetReference } =
      await this.getConfirmBeforeLeave();

    const allowedAction =
      action === 'POP' &&
      this.lastAction === 'POP' &&
      !!location.key &&
      location.pathname !== this.lastLocation?.pathname &&
      location.search === this.lastLocation?.search;

    this.lastAction = action;
    this.lastLocation = location;
    if (allowedAction && !!enable) {
      this._eventService.publish(
        EventNames.shellAnalyticsSimpleUiEvent,
        this.createSimpleUiEventData({
          action: EventDataActionEnum.modalWindowDisplayed,
          controlName: undefined
        })
      );

      if (!this._confirmed) {
        this._confirmed = await this.confirmLeaveWithResumeSessionModal(
          message,
          assetReference
        );
      }

      if (this._confirmed) {
        this._eventService.publish(
          EventNames.shellAnalyticsSimpleUiEvent,
          this.createSimpleUiEventData({
            action: EventDataActionEnum.controlHyperLinkClicked,
            controlName: EventDataControlNameEnum.okButton
          })
        );

        const triggerPath =
          this._storeInterface?.state?.onboarding?.sessionContext
            ?.onboardingContext?.entryUrl;

        if (this._removeConfirmBeforeLeaveListener)
          this._removeConfirmBeforeLeaveListener();

        // entryUrl often points back at a trigger path (e.g. /activate), which
        // SessionManager would otherwise treat as a fresh entry and re-prompt
        // to resume the very session the user just chose to discard.
        window.sessionStorage.setItem(DISCARDED_SESSION_STORAGE_KEY, '1');
        window.location.href = triggerPath || '/';
      } else {
        this._eventService.publish(
          EventNames.shellAnalyticsSimpleUiEvent,
          this.createSimpleUiEventData({
            action: EventDataActionEnum.controlHyperLinkClicked,
            controlName: EventDataControlNameEnum.cancelButton
          })
        );
        this._navigationInterface.goForward();
        return false;
      }
    }
  }

  async startConfirmBeforeLeaveListener(): Promise<void> {
    if (await isNative()) return;
    this._confirmed = false;

    this._navigationInterface.push(
      `${this._navigationInterface.location.pathname}${
        this._navigationInterface.location.search || ''
      }`
    );

    this._removeNavigationBlocker = await this._navigationInterface.block(
      //TODO: Create a new signature for get NavigationPrompt
      //@ts-ignore
      this.getNavigationPrompt
    );

    if (!this._removeConfirmBeforeLeaveListener) {
      const OSDetails = getOSDetailsFromBrowser();
      const isIOS = OSDetails?.name?.toLocaleLowerCase()?.includes('ios');

      if (isIOS) {
        this.setListener('pagehide', this.pagehideCallback);
      } else {
        this.setListener('beforeunload', this.beforeunloadCallback);
      }
    }
  }

  stopConfirmBeforeLeaveListener() {
    if (typeof this._removeConfirmBeforeLeaveListener === 'function') {
      this._removeConfirmBeforeLeaveListener?.();
    }
  }
}
